// Static project content: personal/open-source projects plus hackathon builds
// sourced from Devpost (devpost.com/shaikhatim3).
// Each project renders a card on the site and a full detail page at /project/:id.
//
// Shape:
//   id        URL slug (also used as the React key)
//   title, tagline
//   year, hackathon
//   award     string or null (null = no prize listed)
//   featured  show on the home page highlights grid
//   solo      true if built solo
//   team      collaborators (excluding Shaik)
//   tech      "Built With" list
//   links     { github, demo, website, video, figma, devpost }
//   image     primary thumbnail (or null -> gradient fallback tile)
//   gallery   additional screenshots
//   sections  [{ title, body }]  body is plain text; blank lines split paragraphs

const projects = [
  {
    id: 'sorevo',
    title: 'Sorevo',
    tagline:
      'Describe your trip in plain language and AI builds a living, shareable trip page: itinerary, map, and per-person budget splits. One link that works on the web and opens the iOS app.',
    year: '2026',
    hackathon: 'Personal Project',
    award: null,
    launched: true,
    featured: true,
    solo: true,
    team: [],
    tech: ['SwiftUI', 'iOS', 'React', 'Vite', 'FastAPI', 'Python', 'Supabase', 'PostgreSQL', 'Claude', 'Railway'],
    links: {
      website: 'https://sorevo.app',
    },
    image: null,
    gallery: [],
    sections: [
      {
        title: 'What it is',
        body: "Sorevo is an AI travel planning platform, live now at sorevo.app, and the thing I'm going all-in on next. You describe a trip in plain language, dates, people, vibe, budget, and it generates a rich, shareable trip page: itinerary, map, budget with per-person splits, packing list, and logistics. The page is a living document that evolves as planning actually happens, people join or drop, dates lock, bookings become real. Think Wanderlog, but agentic.",
      },
      {
        title: 'One link, everywhere',
        body: "Sharing is the core of Sorevo. Every trip gets a share link that just works: anyone can open it in a browser and see the full trip page, and on an iPhone the exact same link opens the native app through universal links. Invite links (sorevo.app/j/...) let a friend join the trip crew instantly, with the web page acting as a preview for everyone else. Public links are privacy-aware too: names and money are redacted server-side for non-members, so you can share an itinerary without sharing your budget.",
      },
      {
        title: 'What it does',
        body: "Beyond generating the plan, Sorevo handles the messy group logistics of real trips. Splitwise-style cost splitting is native: expenses, settlements, and balances are computed deterministically on the backend (the AI never touches money math), including per-family subgroup splits for bigger trips. You can attach group-chat screenshots and it mines them for decisions, and a document locker keeps tickets and reservations with the trip, downloadable for offline. Manual planning works too, every trip doesn't need AI.",
      },
      {
        title: 'How I built it',
        body: "The iOS app is SwiftUI (iOS 17+, MVVM with @Observable), and the public web viewer is React + Vite. Behind both sits a FastAPI + SQLAlchemy backend on Railway, with Supabase providing Postgres (row-level security on every table), auth with Apple and Google Sign In, and storage. Trip generation runs on Claude, Sonnet writes the trip, Haiku extracts decisions from screenshots, and every AI call is proxied through the backend so no keys ever ship in a client. The canonical trip state is a versioned, append-only JSON document that both clients render, so history is never lost.",
      },
      {
        title: 'Status',
        body: "Sorevo is launched and in beta: sign up and you get AI-generated and manual trips to plan with, free while I iterate. I'm actively building it right now, alongside Rerun, and it's my next big thing.",
      },
    ],
  },

  {
    id: 'iris',
    title: 'IRIS',
    tagline:
      'An open-source HeyClicky. A Jarvis-style voice assistant for the Mac that sees your screen, speaks as it thinks, and actually gets things done.',
    year: '2026',
    hackathon: 'Personal Project',
    award: null,
    featured: true,
    solo: true,
    team: [],
    tech: ['Swift', 'SwiftUI', 'macOS', 'Claude', 'Claude Code', 'OpenAI TTS', 'Ollama', 'Speech Recognition', 'XcodeGen'],
    links: {
      github: 'https://github.com/Hteam121/IRIS',
    },
    image: null,
    gallery: [],
    sections: [
      {
        title: 'What it is',
        body: "IRIS is my open-source take on HeyClicky, the always-on AI voice assistant for the Mac. I loved the idea of an ambient companion that lives on your desktop, but I wanted one that anyone could inspect, self-host, and extend, with their own keys, their own models, and full control over what it can see and do. So I built one from scratch and released it under the MIT license.\n\nThe assistant itself is called Dory. A small floating dot follows your mouse around the screen. You say \"Hey Dory\" (or hold Option+Space and talk), and it answers out loud while the words stream onto the screen, like Jarvis from Iron Man, but running on your own machine.",
      },
      {
        title: 'From IRIS to Dory',
        body: "The project started life as IRIS, but I renamed the assistant to Dory, partly because I just liked the name better (yes, Finding Dory), and partly because a voice assistant needs one clear, friendly name users can call out without thinking. Juggling two names in the product would be confusing, so everything user-facing is simply Dory: the wake word is \"Hey Dory,\" the app you install is Dory.app, and that's the only name you ever say or see.\n\nUnder the hood, the original IRIS name lives on so existing installs never break: the repo is still IRIS, the bundle id is com.iris.app, settings live in ~/.iris/, and configuration uses IRIS_* environment variables. Permissions, settings, and config all carry over untouched. In short: IRIS is the codebase, Dory is the assistant.",
      },
      {
        title: 'What it does',
        body: 'Dory can look at your screen and explain what it sees, fly an animated arrow to point at things (“where’s the WiFi icon?”), open apps and folders, search the web for real answers, and control your Mac by typing and clicking for you. Ask it to actually do something, like “create a notes file on my desktop”, and a real Claude Code session runs the task in the background with live progress pills under the dot; you can resume or cancel tasks by voice later.\n\nIt has a skills system, too: drop a markdown file in ~/.iris/skills/ and Dory learns a new routine, no rebuild needed. It remembers things you tell it, and it meters every paid API call against a monthly dollar budget you set, automatically falling back to free local paths as you approach the limit.',
      },
      {
        title: 'How I built it',
        body: "IRIS is a native Swift menu-bar app for macOS, no dock icon, just a tiny eye in the menu bar and the dot that follows your cursor. Wake-word detection and speech recognition run fully on-device using Apple's Speech framework, so listening is free and private. The brain is Claude: answers stream in over the Anthropic API (or your Claude subscription via the claude CLI) with prompt caching, so Dory starts speaking the first sentence while the rest is still being written. The natural voice comes from OpenAI's TTS, background agent tasks run as resumable Claude Code sessions, and an optional local model via Ollama answers simple questions for free before anything touches the cloud. A central state machine (CompanionManager) coordinates the whole listen-think-speak-act pipeline.",
      },
      {
        title: 'Why open source',
        body: 'An assistant that can hear your voice, read your screen, and act on your machine should be something you can read the source of. Everything runs locally or through keys you own, your settings and memory never leave your Mac, and the whole thing is MIT-licensed, clone it, change the wake word, swap the models, make it yours.',
      },
    ],
  },

  {
    id: 'jobtrack',
    title: 'JobTrack',
    tagline:
      'Paste a job posting and AI fills in the rest. A kanban-style tracker that follows every application from Saved to Offer, with interview prep built in.',
    year: '2026',
    hackathon: 'Personal Project',
    award: null,
    featured: true,
    solo: true,
    team: [],
    tech: ['React', 'Vite', 'Supabase', 'PostgreSQL', 'OpenAI', 'JavaScript', 'PWA'],
    links: {
      github: 'https://github.com/Hteam121/job-hunt',
      demo: 'https://job-hunt-2rt.pages.dev',
    },
    image: null,
    gallery: [],
    sections: [
      {
        title: 'What it is',
        body: "Job hunting means juggling dozens of applications across spreadsheets, tabs, and memory. JobTrack replaces all of that with one board: paste a job link plus the posting text, and AI extracts the company, title, requirements, and a summary into a card automatically. Every application moves across a status pipeline, Saved, Applied, Interview, Offer, Closed, so you always know exactly where you stand.",
      },
      {
        title: 'What it does',
        body: 'AI auto-fill creates and populates cards in the background while you keep browsing, and bulk add lets you paste several postings at once. The kanban board supports drag-and-drop between stages on desktop and a status dropdown on touch, with multi-select to move batches of cards together and collapsible stages to keep the board tidy. When a role reaches the Interview stage, JobTrack generates tailored interview-prep tips for that specific posting. It ships with light and dark themes, a fully responsive layout, and installs as a PWA.',
      },
      {
        title: 'How I built it',
        body: "The frontend is React 19 with Vite, deployed on Cloudflare Pages. Supabase handles email/password auth and storage, with every row protected by Postgres Row Level Security so users can only ever see their own applications. The AI extraction and interview tips run on OpenAI's gpt-4o-mini, called straight from the browser.",
      },
      {
        title: 'Privacy by design',
        body: "The OpenAI key is bring-your-own: entered at runtime, kept only in the browser tab's sessionStorage, never written to a database or server, never logged, and cleared when the tab closes. Calls go directly from your browser to the OpenAI API, nothing passes through a middleman, and the Supabase client keys are public by design with Row Level Security doing the real protection.",
      },
    ],
  },

  {
    id: 'ghostvision',
    title: 'GhostVision',
    tagline: 'Like a camera, but without the camera. Radar + AI to detect people and falls, privately.',
    year: '2025',
    hackathon: 'HackAI 2025',
    award: 'Winner, Stable Condition track, HackAI 2025',
    featured: true,
    solo: true,
    team: [],
    tech: ['Python', 'C++', 'ESP32', 'Radar', 'FFT', 'OpenCV', 'Machine Learning', 'Raspberry Pi'],
    links: {
      website: 'https://cometpro.xyz',
      video: 'https://youtu.be/hj0sTu7M9G8',
      devpost: 'https://devpost.com/software/ghostvision',
    },
    image:
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/383/341/datas/gallery.jpg',
    gallery: [
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/383/341/datas/gallery.jpg',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/383/340/datas/gallery.jpg',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/381/959/datas/gallery.jpg',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/381/960/datas/gallery.jpg',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/383/339/datas/gallery.jpg',
    ],
    sections: [
      {
        title: 'Inspiration',
        body: "It all started when I came across this crazy project where Wi-Fi routers were being used as makeshift cameras. That got me thinking, what if I could take that concept further using radar instead? No visuals, just pure presence detection, but cooler and more practical. As I explored possible use cases, I realized how valuable this could be in places where cameras and wearables fall short. It reminded me of real situations, like when my grandparents had fallen and no one knew until much later. That's exactly the kind of problem GhostVision could help solve, providing safety without sacrificing privacy. And so, GhostVision was born.",
      },
      {
        title: 'What it does',
        body: 'GhostVision is a radar-based human sensing system that can detect presence and falls without using any traditional cameras or wearables. It turns raw radar signals into heatmaps and uses AI to track people\'s movements in a space. If someone falls, the system detects it instantly and can send an alert. Think of it as a camera that respects your privacy, a ghost camera.',
      },
      {
        title: 'How I built it',
        body: 'I used an ESP32-S3 microcontroller paired with a KLC1a radar sensor to gather real-time data. I synced that with images for training, generating heatmaps using OpenCV and Python. The core detection model uses a U-Net architecture to isolate blobs representing human presence, while a 1D CNN model is trained to detect rapid movement drops as falls. All of this is processed and visualized on a local Flask app running on a Raspberry Pi, no cloud, no Wi-Fi dependency.',
      },
      {
        title: 'Challenges I ran into',
        body: 'Syncing the radar frames and camera frames during data collection was tricky. Handling noise and spike artifacts in FFT outputs from the radar took real effort. Building an efficient fall detection model that works in near real-time, and getting consistent performance with low RAM and CPU usage on the Pi, pushed the limits of the hardware.',
      },
      {
        title: "Accomplishments I'm proud of",
        body: 'Created a fully offline fall detection system with no cameras or wearables. Achieved blob detection with minimal false positives using radar-only data. Designed an intuitive heatmap interface that visualizes presence at ~15 FPS, and ran all AI models locally without relying on the cloud.',
      },
      {
        title: 'What I learned',
        body: 'Radar data is messy but powerful, once cleaned, it can replace cameras in many use cases. Real-time edge AI is hard but doable with the right optimizations. Privacy-first technology can be both functional and futuristic.',
      },
      {
        title: "What's next",
        body: 'Integrate fall alert SMS or push notifications. Expand training datasets with more movement types and make it more accurate to body shapes. Deploy in assisted living centers for testing, and experiment with multiple radar units for multi-person tracking.',
      },
    ],
  },

  {
    id: 'med-x',
    hidden: true,
    title: 'Med-X',
    tagline: 'Find hospitals that accept your insurance, compare costs, and get personalized recommendations, fast, easy, and stress-free.',
    year: '2025',
    hackathon: 'Axxess Hackathon 2025',
    award: null,
    featured: false,
    solo: false,
    team: ['Deric Thomas', 'Hari Ramalingam', 'Krishna Madhekar'],
    tech: ['React', 'Flask', 'Python', 'JavaScript', 'Google Maps', 'Gemini', 'Firebase', 'Bash'],
    links: {
      github: 'https://github.com/Hteam121/med-x',
      video: 'https://youtu.be/HDj7fY6mW-M',
      devpost: 'https://devpost.com/software/med-x-240ptd',
    },
    image:
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/283/269/datas/original.png',
    gallery: [
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/283/269/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/283/272/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/283/276/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/283/277/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/282/927/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/283/278/datas/original.png',
    ],
    sections: [
      {
        title: 'What it does',
        body: 'Navigating healthcare is often confusing, especially when it comes to understanding insurance coverage and finding the right hospital. Many people face unexpected medical bills and struggle to find hospitals that accept their insurance. MedX simplifies hospital selection by providing transparent information on cost, coverage, and accessibility, making healthcare decisions easier, faster, and stress-free.',
      },
      {
        title: 'How we built it',
        body: 'MedX was developed using React.js for the frontend and Flask for the backend, ensuring a lightweight and efficient system. We integrated the Google Maps API to fetch hospital locations and provide users with a clear, interactive map view. The user flow is simple: select your insurance, enter a medical procedure, and receive a list of hospitals filtered by price, distance, and ratings.',
      },
      {
        title: 'Accomplishments we are proud of',
        body: 'One of our biggest achievements was building a custom API that connects insurance data with hospital searches. We successfully optimized hospital search filters, allowing users to refine results based on distance, rating, and price, all while keeping the interface responsive. Despite facing API rate limits and backend deployment challenges, we shipped a functional system that improves healthcare decision-making.',
      },
      {
        title: 'Challenges we ran into',
        body: 'Our biggest challenge was insurance data limitations, since the Google Places API does not say whether a hospital accepts a specific plan, we built workarounds and filtering methods. Optimizing search performance meant balancing speed with accuracy, and deploying the Flask backend required troubleshooting CORS issues and handling API rate limits.',
      },
      {
        title: "What's next",
        body: 'We plan to enhance MedX with AI-powered recommendations that factor in injury severity and urgency, integrate real-time price estimates for procedures, and expand coverage for more insurance providers.',
      },
    ],
  },

  {
    id: 'hi-5',
    title: 'Hi-5',
    tagline: 'A smart glove that uses servos and voice control to aid rehabilitation, physical therapy, and assist paralyzed limbs.',
    year: '2025',
    hackathon: 'TAMUhack 2025',
    award: 'Winner, Best Overall Hardware Hack, TAMUhack 2025',
    featured: true,
    solo: true,
    team: [],
    tech: ['Arduino', 'Raspberry Pi', 'React', 'Firebase', 'Python'],
    links: {
      website: 'https://cometpro.xyz',
      video: 'https://youtu.be/bsLTez6VdQU',
      devpost: 'https://devpost.com/software/hi-5-lng2ha',
    },
    image:
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/232/997/datas/original.png',
    gallery: [
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/232/997/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/233/000/datas/original.png',
    ],
    sections: [
      {
        title: 'Inspiration',
        body: 'My inspiration came from watching Iron Man as a kid. The famous line, "Tony Stark built this in a cave... with a box of scraps!" stuck with me, showing how creativity and determination can overcome any obstacle. It made me believe, "If he can build it there, then so can I." Now, as I approach graduation, creating something like this has been a lifelong dream, and I\'m proud to have turned it into reality and see it fully functional.',
      },
      {
        title: 'What it does',
        body: 'Hi-5 is a versatile assistive glove designed to help individuals with limited hand mobility caused by conditions such as stroke recovery, neurological disorders, arthritis, or paralysis. By aiding rehabilitation, physical therapy, and assisting paralyzed limbs, Hi-5 helps users regain strength, precision, and control. The voice control feature makes it incredibly user-friendly, allowing movements through simple commands even with minimal mobility. While this prototype focuses on the hand, the concept can extend to other limbs.',
      },
      {
        title: 'How I built it',
        body: 'The glove uses elastic strings attached to servo motors that mimic tendon-like movements, sewn onto the glove to facilitate natural finger movement. The servos connect to a servo motor driver wired to an Arduino Uno, which listens for serial commands dictating which fingers to open or close. These commands travel via USB to a Raspberry Pi 5 acting as an intermediary processor, which updates finger movements based on changes in a Firebase Realtime Database. A ReactJS front end lets users input commands, and for voice control I used a React NLP library that normalizes commands and maps them to finger movements.',
      },
      {
        title: 'Challenges I ran into',
        body: 'There were numerous challenges. Learning how to sew the elastic strings onto the glove while maintaining durability was unexpectedly tricky. Understanding servo motor mechanics, balancing power requirements, and overcoming technical issues were ongoing hurdles. Each time I fixed one problem, another would arise, but persistence kept me going.',
      },
      {
        title: "Accomplishments I'm proud of",
        body: "This was my first solo hackathon project, and I'm incredibly proud of how much I accomplished in such a short time. It was rewarding to see all the skills I've learned over the years, programming, hardware integration, and problem-solving, come together in one functional product.",
      },
      {
        title: 'What I learned',
        body: 'This project was my first deep dive into mechatronics and working with servo motors, a steep but exciting learning curve. I also picked up unexpected skills, like sewing elastic components, while gaining hands-on experience integrating hardware to cloud databases.',
      },
      {
        title: "What's next",
        body: 'Add more sensors for feedback and adaptive functionality, deliver greater power and precision for faster reaction times, refine the design for comfort, and expand its applications beyond the hand to other limbs.',
      },
    ],
  },

  {
    id: 'checkpoint',
    title: 'Checkpoint',
    tagline: 'Smart school pickups made easy, scan license plates, verify drivers, and notify teachers and parents in real time.',
    year: '2024',
    hackathon: 'HackUTA 6',
    award: null,
    featured: false,
    solo: false,
    team: ['Sahishnu Sagiraju', 'Adit Pathania', 'Sherwin Thirumavalan'],
    tech: ['Python', 'YOLOv8', 'EasyOCR', 'Raspberry Pi', 'Firebase', 'Google Cloud', 'React', 'Figma'],
    links: {
      figma: 'https://www.figma.com/proto/mlZnXsEuCTh1hXdi5YVFDI/Checkpoint?node-id=0-1',
      devpost: 'https://devpost.com/software/checkpoint-z4vt73',
    },
    image:
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/073/681/datas/original.gif',
    gallery: [
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/073/681/datas/original.gif',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/078/600/datas/original.gif',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/073/544/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/073/583/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/078/420/datas/original.png',
    ],
    sections: [
      {
        title: 'Inspiration',
        body: 'The idea came from the need to improve security during school pickups, where unauthorized individuals could potentially pick up students. We wanted a seamless, tech-driven solution that ensures student safety while making pickup more efficient and stress-free for parents and schools.',
      },
      {
        title: 'What it does',
        body: 'The system uses object detection and license plate recognition to verify vehicles in school pickup lines. It scans cars, checks them against a secure database, and notifies teachers when the right car arrives. Parents get real-time updates when their child checks in or out, and guest pickups are securely verified through a driver-side app.',
      },
      {
        title: 'How we built it',
        body: 'A Raspberry Pi 5 manages the real-time camera feed for object detection and license plate recognition. YOLOv8 was optimized to detect vehicles and zoom in on plates, and EasyOCR reads the plate characters. The detected text is cross-referenced with a Firebase Realtime Database of authorized vehicles; on a match, the system sends the student\'s name and verification status to a frontend for teachers. A driver-side app prototype, built in Figma, lets parents register vehicles by scanning government IDs verified by school administration. The whole system works in real time across the Pi, Firebase, and frontend.',
      },
      {
        title: 'Challenges we ran into',
        body: 'Integrating object detection and OCR within hackathon time was hard. Optimizing YOLOv8 for plates was tricky under varying lighting and odd angles, requiring fine-tuning and retraining. Real-time updates between the Pi, Firebase, and frontend needed low-latency communication without delays, and EasyOCR struggled with dirty, reflective, or partially obstructed plates, so we experimented with image preprocessing to improve accuracy.',
      },
      {
        title: 'Accomplishments we are proud of',
        body: 'We built a working model that scans cars, reads license plates, and a prototype demo that verifies authorized pickups and updates a real-time frontend.',
      },
      {
        title: "What's next",
        body: 'Enhance scalability, improve the UI, and add more security features. We aim to integrate a comprehensive dashboard for school administrators to manage pickup records, notifications, and guest approvals.',
      },
    ],
  },

  {
    id: 'nowtera',
    hidden: true,
    title: 'NowTera',
    tagline: 'Unites classrooms with AI-powered tools, focus tracking, and seamless resource sharing, transforming collaborative learning.',
    year: '2024',
    hackathon: 'HackUTD 2024: Ripple Effect',
    award: null,
    featured: false,
    solo: false,
    team: ['Hari Ramalingam', 'Sherwin Thirumavalan'],
    tech: ['React', 'Python', 'OpenCV', 'TensorFlow Lite', 'Raspberry Pi', 'Pinata / IPFS', 'Kommunicate', 'Figma'],
    links: {
      figma: 'https://www.figma.com/design/bfZARpSKrCVFHqHN9M5bCF/NOWTERA?node-id=0-1',
      video: 'https://youtu.be/mUGKukNUyWA',
      devpost: 'https://devpost.com/software/nowtera',
    },
    image:
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/144/284/datas/gallery.jpg',
    gallery: [
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/144/284/datas/gallery.jpg',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/140/892/datas/gallery.jpg',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/140/893/datas/gallery.jpg',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/144/311/datas/gallery.jpg',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/144/324/datas/gallery.jpg',
    ],
    sections: [
      {
        title: 'Inspiration',
        body: 'Traditional learning platforms fail to unify collaboration, resource sharing, and productivity. Students juggle multiple tools, while teachers lack streamlined communication with their classes. NowTera bridges these gaps by creating a centralized hub for dynamic, collaborative, and focused learning.',
      },
      {
        title: 'What it does',
        body: 'NowTera is an all-in-one education platform. It offers centralized communication through dedicated student, teacher, and TA channels; AI-powered study sessions with real-time eye-tracking on a Raspberry Pi to monitor focus, with encouraging feedback via LED flashes and vibration alerts; decentralized, secure file sharing powered by Pinata/IPFS; a custom AI chatbot that builds study plans trained on user-uploaded files; and collaborative study sessions with RSVP tracking.',
      },
      {
        title: 'How we built it',
        body: 'The frontend is ReactJS with interactive modals and real-time updates. The backend uses Pinata IPFS for decentralized file storage and a Kommunicate chatbot for specialized learning. On the AI and hardware side, TensorFlow Lite handles real-time focus tracking on a Raspberry Pi 5, with GPIO peripherals (LEDs, vibration motors) for feedback.',
      },
      {
        title: 'Challenges we faced',
        body: 'Real-time hardware synchronization, aligning eye-tracking data with physical feedback, required precise timing. Managing secure file uploads and dynamic lists from the Pinata API was complex, and ensuring seamless collaboration for large classes while minimizing upload-limit complexity was a balancing act.',
      },
      {
        title: 'Accomplishments',
        body: 'Built an intuitive, engaging UI; integrated decentralized file storage with real-time updates via Pinata; designed a functional AI chatbot trained on user documents; and delivered a hardware-driven focus tracker that motivates and improves productivity.',
      },
      {
        title: "What's next",
        body: 'Expand NowTera into a mobile app, introduce deeper personalized learning insights and step-by-step problem solving, and add gamification with achievements and rewards.',
      },
    ],
  },

  {
    id: 'paralavi',
    hidden: true,
    title: 'Paralaví',
    tagline: 'An AI-driven app that optimizes grocery shopping by comparing store prices, suggesting cheaper stores, and predicting future expenses.',
    year: '2024',
    hackathon: 'HackTX 2024',
    award: null,
    featured: false,
    solo: false,
    team: ['Sherwin Thirumavalan', 'Adit Pathania', 'Sahishnu Sagiraju'],
    tech: ['Python', 'Intel OpenVINO', 'IPEX', 'InterSystems IRIS', 'Computer Vision', 'EasyOCR', 'SQL', 'Figma'],
    links: {
      github: 'https://github.com/Hteam121/paralavi',
      figma: 'https://www.figma.com/design/JdjoPvaFrAnK4qbK8hhnbf/Paralavi.?node-id=0-1',
      devpost: 'https://devpost.com/software/paralavi',
    },
    image:
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/116/049/datas/original.gif',
    gallery: [
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/116/049/datas/original.gif',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/112/663/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/112/666/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/112/667/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/116/154/datas/original.png',
    ],
    sections: [
      {
        title: 'Inspiration',
        body: "Imagine it's Saturday morning and you have your grocery list in hand. Like every week, you'll hit Costco, Walmart, and Kroger. But say you need tomatoes, they're at each store, so how do you know which has the best price? This is where Paralaví steps in. Instead of guessing, its algorithm searches for the best deals across your chosen stores, compares prices in real time, and even maps out the most efficient route, turning every shopping trip into a smart shopping strategy.",
      },
      {
        title: 'What it does',
        body: 'Paralaví is a receipt management and shopping optimization app that uses AI to recommend the best store deals and track spending. With OCR, it extracts details from scanned receipts, categorizes expenses, and suggests budget-friendly shopping options across stores like Costco, Kroger, and Walmart.',
      },
      {
        title: 'How we built it',
        body: "Paralaví leverages Intel's OpenVINO for high-speed OCR that converts receipt images into structured data, IPEX for training a price-optimization model that analyzes shopping habits and price trends, and InterSystems IRIS for data storage and vector searches to quickly retrieve similar items and prices. We built and ran the model on the Intel Tiber Developer Cloud Jupyter Notebook.",
      },
      {
        title: 'Challenges we ran into',
        body: "Our biggest challenge was fine-tuning the OCR model to accurately process varied receipt formats. Integrating InterSystems IRIS for real-time, similarity-based searches also required significant data structuring, which was often hard to get right.",
      },
      {
        title: 'Accomplishments we are proud of',
        body: "Enhanced OCR performance with Intel's OpenVINO for fast, reliable receipt extraction; an effective price-optimization model that learns from historical data; and a user-centric interface with real-time insights that make budgeting and shopping easier.",
      },
      {
        title: "What's next",
        body: 'Deploy Paralaví to the cloud for broader accessibility, add more stores, integrate budget alerts, and enhance the AI model for more precise price predictions and personalized recommendations.',
      },
    ],
  },

  {
    id: 'utilitrack',
    title: 'UtiliTrack',
    tagline: 'Automates maintenance scheduling, tracks feedback, and monitors energy usage to help facilities cut costs, potentially saving $111B in the US.',
    year: '2024',
    hackathon: 'HackUNT 2024',
    award: 'Winner, CBRE Track · 3rd Overall, HackUNT 2024',
    featured: true,
    solo: false,
    team: ['Sahishnu Sagiraju', 'Hari Ramalingam', 'Sid Mylavarapu'],
    tech: ['React', 'Python', 'Firebase', 'Machine Learning'],
    links: {
      github: 'https://github.com/Sid-Myla/HackUNT2024/tree/good',
      devpost: 'https://devpost.com/software/utilitrack',
    },
    image:
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/103/365/datas/original.png',
    gallery: [
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/103/365/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/103/552/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/103/553/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/103/554/datas/original.png',
    ],
    sections: [
      {
        title: 'Inspiration',
        body: "UtiliTrack was inspired by a friend's struggle to manage energy costs in his family-run restaurant. During peak summer months, he was losing between $3,000 and $5,000 monthly just on air conditioning, and the wear and tear led to vent leaks and damage. His experience opened our eyes to widespread infrastructure inefficiencies. With an annual $370 billion in energy waste in the U.S., we set out to build a solution.",
      },
      {
        title: 'What it does',
        body: 'UtiliTrack gives businesses a tool to manage energy usage and streamline maintenance. It continuously monitors consumption patterns and detects inefficiencies, letting systems like HVAC run only when necessary. By integrating automated scheduling and sensor feedback, it adjusts power usage dynamically, saving costs, reducing strain on equipment, and managing operational efficiency from a single dashboard.',
      },
      {
        title: 'How we built it',
        body: 'We built UtiliTrack from the ground up using programming, data analysis, and sensor integration. We coded functions that track real-time energy consumption and algorithms that identify waste patterns. The backend supports easy integration with existing infrastructure, while the front end offers a user-friendly dashboard. We also incorporated machine learning to optimize settings based on usage data, making UtiliTrack smarter over time.',
      },
      {
        title: 'Challenges we ran into',
        body: 'Integrating seamlessly with varied business infrastructures required deep customization and testing. Ensuring accuracy in real-time energy data collection was another hurdle, and we had to refine predictive algorithms to operate reliably across different systems and industries, all while balancing automation with user control.',
      },
      {
        title: 'Accomplishments we are proud of',
        body: "UtiliTrack's ability to make a real impact on energy costs and operational efficiency. We're proud of the user-friendly dashboard that lets non-technical users navigate data insights effortlessly, and of testing it in real scenarios where it detected and prevented unnecessary energy expenditure.",
      },
      {
        title: "What's next",
        body: 'Expand with advanced predictive maintenance to proactively address issues before breakdowns, integrate IoT for real-time responsiveness, and tailor features to specific industries with customizable options.',
      },
    ],
  },

  {
    id: 'park-ez',
    title: 'Park-EZ',
    tagline: 'Computer vision that detects accessible parking spots in real time, delivering instant availability for a stress-free parking experience.',
    year: '2024',
    hackathon: 'HackSMU VI',
    award: 'Winner, OIT Track, HackSMU VI',
    featured: true,
    solo: false,
    team: ['Adit Pathania', 'Sherwin Thirumavalan', 'Sahishnu Sagiraju'],
    tech: ['Python', 'YOLO', 'OpenCV', 'Raspberry Pi', 'Arduino', 'React', 'Firebase'],
    links: {
      github: 'https://github.com/sahisagiraju/parkez/tree/main',
      figma: 'https://www.figma.com/proto/enaklGdGeywp96xJgJqhEk/HackSMU-VI?node-id=0-1',
      video: 'https://youtu.be/Opd_XGVUmNw',
      devpost: 'https://devpost.com/software/park-ez',
    },
    image:
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/065/958/datas/original.png',
    gallery: [
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/065/958/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/065/901/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/065/903/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/003/066/237/datas/original.jpeg',
    ],
    sections: [
      {
        title: 'Inspiration',
        body: "At Park-EZ, we believe accessibility should never be a guessing game. Our inspiration came from recognizing the everyday challenges disabled drivers face finding parking, especially in busy areas like hospitals or garages. We set out to use computer vision to detect accessible vehicles while building a platform that streamlines the parking experience for disabled users.",
      },
      {
        title: 'What it does',
        body: 'Park-EZ detects the International Symbol of Access (ISA) on license plates or windows in video streams, fast, lightweight, and accurate. Using open datasets, it outputs bounding boxes around detected symbols with confidence scores, processing at 30fps without dropped frames. On top of detection, an accessible platform lets disabled users check the availability of accessible spots in real time, saving time and stress in multi-level garages or large hospital lots.',
      },
      {
        title: 'How we built it',
        body: 'We trained our model on open computer vision datasets to detect ISA symbols on vehicles in real time, outputting bounding boxes and confidence scores. To demonstrate it, we built a simulation using a Raspberry Pi 5 connected to LEDs indicating spot availability, with a web dashboard for monitoring. The accessible web platform lets disabled users check availability remotely and plan their trips.',
      },
      {
        title: 'Challenges we ran into',
        body: 'The biggest challenge was processing video streams fast enough to hold 30fps without dropping frames while keeping high accuracy detecting the ISA symbol. Integrating the Raspberry Pi simulation into the web platform and keeping the dashboard accurately in sync with real-time availability was another hurdle.',
      },
      {
        title: 'Accomplishments we are proud of',
        body: 'Achieving real-time detection under 33ms per frame, building a platform with the potential to make parking more accessible for disabled individuals, and creating a small-scale Raspberry Pi simulation demonstrating the concept in action.',
      },
      {
        title: "What's next",
        body: 'Let users reserve spots in advance or get notified when accessible parking becomes available nearby, add predictive spot availability via ML, and extend the simulation to real-world deployment with parking facilities.',
      },
    ],
  },

  {
    id: 'haven',
    title: 'Haven',
    tagline: 'Transforms house hunting with a swipe, personalized home matches via AI validation scores for buyers and targeted leads for realtors.',
    year: '2023',
    hackathon: 'HackUTD X',
    award: 'Winner, Fannie Mae Track, HackUTD X',
    featured: true,
    solo: false,
    team: ['Sahishnu Sagiraju', 'Hari Ramalingam', 'Sherwin Thirumavalan'],
    tech: ['Python', 'Scikit-learn', 'Google', 'Figma'],
    links: {
      video: 'https://youtu.be/h9ZoCr7XUc8',
      devpost: 'https://devpost.com/software/haven-yxpujc',
    },
    image:
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/658/031/datas/gallery.jpg',
    gallery: [
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/658/031/datas/gallery.jpg',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/660/093/datas/gallery.jpg',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/660/096/datas/gallery.jpg',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/660/097/datas/gallery.jpg',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/660/098/datas/gallery.jpg',
    ],
    sections: [
      {
        title: 'Inspiration',
        body: "Inspired by the swipe-right culture of modern dating apps, we dreamed up Haven, a fun, user-friendly app where finding your dream home is as simple as finding a date. Fueled by Fannie Mae's dedication to making housing more accessible, we made the home-buying process less daunting and a lot more personal. This is for everyone who's ever felt lost in the sea of real estate.",
      },
      {
        title: 'What it does',
        body: "For buyers, Haven kicks off with a quick financial check-up to determine a validation score that guides how ready they are to buy, with personalized advice to improve buying capacity, then swiping through homes to find the perfect match. For realtors, a dashboard tracks listing performance, shows who's interested, and lets them prioritize and reach out to the most qualified buyers, turning swipes into sales.",
      },
      {
        title: 'How we built it',
        body: 'We created a model to predict the validation score. After cleaning and preparing the data, we engineered financial indicators like LTV, DTI, and FEDTI ratios as predictive features, then chose a Random Forest algorithm for its ability to handle complex datasets. We tuned the model for accuracy and reliability, and used Figma to design the UI.',
      },
      {
        title: 'Challenges we ran into',
        body: 'Our challenges were data analysis, algorithm optimization, front-end integration, and time management. We had to learn terms like LTV, FEDTI, and DTI to build an algorithm that predicts a good score. The front end was challenging to build in limited time, so we switched to Figma to create a presentable, functioning UI.',
      },
      {
        title: 'Accomplishments we are proud of',
        body: 'Understanding the data well enough to build a better validation-score model, successfully creating a working design in Figma, and learning the process of building an app from the ground up.',
      },
      {
        title: "What's next",
        body: 'Incorporate more financial metrics for precise advice, add a real-time analytics dashboard and integrated mortgage calculators, explore gamification and a community forum, and scale Haven to new markets.',
      },
    ],
  },

  {
    id: 'dermaskan',
    title: 'DermaSkan',
    tagline: 'Snap a pic; know your skin. Instant AI skin-condition analysis and treatment suggestions, skincare made easy.',
    year: '2024',
    hackathon: 'TAMUhack X',
    award: null,
    featured: false,
    solo: false,
    team: ['Sherwin Thirumavalan', 'Sahishnu Sagiraju', 'Adit Pathania'],
    tech: ['React', 'Flask', 'Python', 'TensorFlow', 'Firebase', 'Firestore', 'API'],
    links: {
      github: 'https://github.com/Hteam121/DermaSkan-frontend',
      website: 'https://citruscode.app',
      devpost: 'https://devpost.com/software/dermaskan',
    },
    image:
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/742/743/datas/original.png',
    gallery: [
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/742/743/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/742/951/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/742/744/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/742/745/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/742/785/datas/original.png',
    ],
    sections: [
      {
        title: 'Inspiration',
        body: 'Nearly 1 in 3 Americans suffer from some form of skin disease, an alarming statistic. Conditions like skin cancer, if detected early, can be largely preventable, but in busy day-to-day life we may overlook early signs. Inspired by the prevalence of skin ailments, we aimed to build a solution that monitors conditions and provides accurate diagnoses, especially to those who lack access.',
      },
      {
        title: 'What it does',
        body: "Users capture images of their skin with their device's camera. The images are sent to our backend via our API, processed by our VGG machine learning model and the GPT API to generate an accurate diagnosis, and returned to the frontend. The information is securely linked to each account through login authentication, empowering users to seek medical assistance without privacy concerns.",
      },
      {
        title: 'How we built it',
        body: 'We started with a user-friendly frontend designed for all age groups. We integrated Firebase for secure login authentication and Firestore for image storage and scan tracking. We developed a VGG (Visual Geometry Group) model in Google Colab, achieving a 99% reliability score, then connected it to a React frontend hosted on an Apache server.',
      },
      {
        title: 'Challenges we ran into',
        body: 'Optimizing our model in Colab was time-sensitive, with each iteration taking 30–40 minutes. Creating our own API presented difficulties, and we had to implement CRUD operations and ensure resiliency to fully meet the project guidelines. These challenges pushed us to innovate and problem-solve.',
      },
      {
        title: 'Accomplishments we are proud of',
        body: 'Despite initial brainstorming struggles, we pivoted successfully, showcasing adaptability. We integrated a VGG model, GPT API, and Flask server into a cohesive backend, designed a minimalist UI, and built a product that reflects our dedication to going above and beyond.',
      },
      {
        title: "What's next",
        body: 'Improve accuracy by expanding our dataset, add whole-body skin scans so subtle changes go unnoticed no longer, and collaborate with pharmaceutical companies to provide tailored medical remedy suggestions.',
      },
    ],
  },

  {
    id: 'peak',
    hidden: true,
    title: 'PEAK',
    tagline: 'Power Efficiency and Awareness Kit, analytics, predictive maintenance, and cost savings for your home energy usage.',
    year: '2023',
    hackathon: 'HackUTA 2023',
    award: null,
    featured: false,
    solo: false,
    team: ['Sherwin Thirumavalan', 'Deric Thomas', 'Sahishnu Sagiraju'],
    tech: ['React', 'Python', 'NumPy', 'Google Colab', 'ARIMA'],
    links: {
      github: 'https://github.com/Hteam121/peak-final',
      devpost: 'https://devpost.com/software/peak-go43u9',
    },
    image:
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/615/136/datas/original.png',
    gallery: [
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/615/136/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/615/139/datas/original.jpeg',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/615/339/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/615/340/datas/original.png',
    ],
    sections: [
      {
        title: 'Inspiration',
        body: 'With electricity costs projected to rise, Americans need to be more aware of their power expenditure. Tracking it leads to more efficient use and benefits the world, less electricity use means less pollution from non-renewable sources. With so many appliances and devices throughout a home, it can be hard to keep track of them all, so PEAK uses machine learning to streamline monitoring and maintenance while giving clients clear insight into power usage.',
      },
      {
        title: 'What it does',
        body: 'PEAK has four main parts: a dashboard with analytics for average energy usage per room and predictive usage over the year; an energy page showing power usage for all devices; a quality-check page using an ARIMA model to predict whether sensors or devices need inspection within 15 days; and a cost-savings page showing cost over time, savings per month, and advice to reduce costs, plus settings and profile pages for push notifications and account customization.',
      },
      {
        title: 'How we built it',
        body: "We developed the frontend in ReactJS, breaking pages into components so each team member could focus on a different part. For the backend, we used Google Colab to build multiple models from three years' worth of data from Arlington homes, enabling precise recommendations for energy consumption and preventive measures.",
      },
      {
        title: 'Challenges we ran into',
        body: 'Large datasets were hard to find, so we used NumPy to generate ample data points from our original set, ensuring the data made sense in real time (e.g., more AC usage in summer). We used an ARIMA model to predict device quality from filter cleanliness, battery health, and time since last repair. Building customized React graphs was difficult, they would clear after deploying, and deploying to our domain (sfpeak.tech, for State Farm PEAK) added challenges.',
      },
      {
        title: 'Accomplishments we are proud of',
        body: 'Simply submitting a finished product. We nearly quit around 6 PM out of frustration with no progress, but after a break we used newfound zeal to make tremendous progress. Despite limited data, our generated dataset was accurate, with trends that logically make sense, and we feel the UI/UX may be our best yet.',
      },
      {
        title: "What's next",
        body: 'Scale our services to larger homes, offices, and enterprises; gather a larger dataset for better predictions and less bias; and use our ML model to actively control devices, adjusting thermostats and toggling devices to maximize automated cost efficiency, customized per user.',
      },
    ],
  },

  {
    id: 'toolboxpro',
    title: 'toolboxpro.tech',
    tagline: 'Vent, connect, and find support in the construction community, real-time peer chat and AI-assisted mental health conversations.',
    year: '2023',
    hackathon: 'HackTX 2023',
    award: null,
    featured: false,
    solo: false,
    team: ['Sherwin Thirumavalan', 'Adit Pathania', 'Sahishnu Sagiraju'],
    tech: ['React', 'Firebase', 'Firestore', 'Google Cloud', 'DialogFlow', 'NLP', 'Realtime Database'],
    links: {
      github: 'https://github.com/Hteam121/toolbox',
      website: 'https://toolboxpro.tech',
      devpost: 'https://devpost.com/software/toolboxpro-tech',
    },
    image:
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/635/530/datas/original.gif',
    gallery: [
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/635/530/datas/original.gif',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/635/601/datas/original.gif',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/635/403/datas/original.PNG',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/635/459/datas/original.png',
    ],
    sections: [
      {
        title: 'Inspiration',
        body: 'After a long day of work, family issues, financial struggles, and other challenges, everyone wants to vent to someone who can relate, or at least someone who will listen. Construction workers face unique stressors that impact their mental health. Toolbox was formed to create a supportive platform where they can share experiences, vent frustrations, and find a listening ear, whether through a fellow worker or a supportive AI.',
      },
      {
        title: 'What it does',
        body: 'Toolbox is a safe space for construction workers to express concerns, share day-to-day experiences, or vent without strings attached. It provides anonymous real-time chatting with other workers or an AI chatbot trained in mental health support. When a mutual connection is made between peers, they can choose to connect further through third-party apps. Toolbox promotes a safe, supportive community within the construction industry.',
      },
      {
        title: 'How we built it',
        body: 'Using React for the frontend and Firebase for the real-time database, we crafted an intuitive interface with anonymous authentication. The chatbot was trained on a dataset tailored for construction workers\' mental health on DialogFlow, ensuring relevant, considerate responses, and we hosted the app at toolboxpro.tech.',
      },
      {
        title: 'Challenges we ran into',
        body: "Designing the chatbot's real-time input-output fields in Firebase was hard to configure. Training DialogFlow to be considerate around mental health terminology was tricky, and since we couldn't import our dataset directly, we spent a lot of time manually importing it piece by piece.",
      },
      {
        title: 'Accomplishments we are proud of',
        body: 'Integrating everything into one web app using Google Cloud as the backend and React on the front end, our first time using Google Cloud. We grew in team communication, with every member contributing, and are proud to have addressed ProCore\'s problem statement with a safe space for construction workers.',
      },
      {
        title: "What's next",
        body: 'Expand Toolbox with a more tailored AI experience over time, add group forums and features for unique stressors in the construction community, support audio/video calls, and enable connecting further anonymously through Toolbox itself.',
      },
    ],
  },

  {
    id: 'citrus',
    title: 'Citrus',
    tagline: 'AI-powered predictive maintenance. Predict. Prevent. Protect.',
    year: '2023',
    hackathon: 'HackSMU V',
    award: 'Winner, Best Pitch, HackSMU V',
    featured: true,
    solo: false,
    team: ['Sahishnu Sagiraju', 'Sherwin Thirumavalan'],
    tech: ['React', 'JavaScript', 'Python', 'Google Colab', 'ARIMA', 'CentOS', 'Figma'],
    links: {
      website: 'https://citruscode.app/',
      devpost: 'https://devpost.com/software/citrus-rbz8xs',
    },
    image: null,
    gallery: [],
    sections: [
      {
        title: 'Background',
        body: 'Modern buildings like malls, offices, hospitals, and schools are intricate ecosystems filled with devices controlling everything from temperature to security. Managing these devices, especially specialized equipment in places like hospitals, is complex. Citrus leverages sensors, machine learning, and active anomaly detection to automate monitoring and maintenance, ensuring smoother daily operations and enhanced safety.',
      },
      {
        title: 'What it does',
        body: 'Citrus harnesses real-time sensor data and an ARIMA model to predict the likelihood of device failures within the next month, enabling preventative action before issues arise. It also offers a database-like interface that simplifies monitoring and filtering through thousands of sensors, making complex building systems easier to manage.',
      },
      {
        title: 'Dataset & backend',
        body: 'We created a mock dataset with 1000 elements per building type, resembling the dataset provided by CBRE and covering various building assets. We ran our ARIMA (AutoRegressive Integrated Moving Average) model to calculate the probability of asset failure within the next month, assigned a binary value to indicate likely failure, and exported the processed data as CSV for front-end integration.',
      },
      {
        title: 'Frontend',
        body: 'We implemented a charting system that visualizes specific CSV data points, which the ARIMA model further processes to calculate failure probabilities and forecasts. Features include a ranking system for assets based on failure probability, search boxes for filtering, and a "Generate Report" button for a streamlined overview of asset conditions.',
      },
      {
        title: 'Challenges we faced',
        body: 'Dealing with inherent differences between building types, hospitals have many rooms with specialized equipment, while malls have open spaces, made writing dynamic, adaptable code challenging. Our mock dataset was initially skewed toward higher failure probabilities, risking an over-alerting model that could dilute genuinely critical warnings.',
      },
      {
        title: "What's next",
        body: 'Integrate an advanced scheduling system with a chatbot for maintenance requests, develop a CNN model to detect real-time issues from images or descriptions, and analyze energy consumption to optimize building energy use.',
      },
    ],
  },

  {
    id: 'pocket-lawyer',
    title: 'Pocket Lawyer',
    tagline: "And that's why we'll fight for you, Richardson, better call H-Team! Legal literacy and protection in your pocket.",
    year: '2023',
    hackathon: 'HackAI 2023',
    award: null,
    featured: false,
    solo: false,
    team: ['Sahishnu Sagiraju', 'Hari Ramalingam', 'Sherwin Thirumavalan'],
    tech: ['Python', 'Flask', 'Respell', 'HTML', 'CSS', 'JavaScript', 'DreamHost'],
    links: {
      github: 'https://github.com/Hteam121/pocket-lawyer-1',
      website: 'https://chatphd.co',
      video: 'https://youtu.be/IdYUliupWgo',
      devpost: 'https://devpost.com/software/pocket-lawyer-kupzes',
    },
    image:
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/453/871/datas/original.png',
    gallery: [
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/453/871/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/453/872/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/453/873/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/453/874/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/453/875/datas/original.png',
    ],
    sections: [
      {
        title: 'Background',
        body: 'The law is essential to daily life, but its complex language often makes it difficult for the average person to understand. This lack of legal literacy can put individuals at a disadvantage when interacting with police officers and other authorities.',
      },
      {
        title: 'Objective',
        body: 'Our goal is to prevent abuse of power and promote legal literacy, helping both civilians and officers communicate more effectively. Minority groups in particular may struggle with legal language during police interactions, leading to misunderstandings and unnecessary fear. The app bridges communication gaps and fosters positive relationships. Inspiration came from videos about police recording restrictions, incidents of excessive force, and minority community struggles with law enforcement.',
      },
      {
        title: 'Functionality',
        body: "Record, audio input using Respell's Whisper API for live transcription with automatic English translation; GPT-4 chat-completion searches for state law keywords and outputs full legal text. Output, summarized legal information via a GPT text-edit model with bullet points and official law names/sections. Panic Mode, press panic to stream camera and audio to three emergency contacts, creating witness documentation. Settings, language selection for better transcription, with text or audio output to accommodate literacy challenges.",
      },
    ],
  },

  {
    id: 'chatphd',
    hidden: true,
    title: 'chatphd.co',
    tagline: "Not feeling well? Unsure what it could be? ChatPHD's symptom detector uses AI models to produce an accurate diagnosis, treatment, a click away.",
    year: '2023',
    hackathon: 'Axxess Hackathon 2023',
    award: null,
    featured: false,
    solo: false,
    team: ['Sahishnu Sagiraju', 'Sherwin Thirumavalan', 'Hari Ramalingam'],
    tech: ['Python', 'Django', 'HTML', 'CSS', 'JavaScript', 'DreamHost'],
    links: {
      website: 'https://chatphd.co/',
      devpost: 'https://devpost.com/software/chatphd-co',
    },
    image:
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/437/409/datas/original.png',
    gallery: [
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/437/409/datas/original.png',
      'https://d112y698adiu2z.cloudfront.net/photos/production/software_photos/002/437/410/datas/original.png',
    ],
    sections: [
      {
        title: 'What it does',
        body: 'As we looked for project ideas, we wanted something innovative and challenging, that is where chatPHD came from. The name may throw you off (there is no correlation), but our product is a simple AI tool that takes in symptoms you may have and outputs related diseases. It also provides medications to take and a short list of useful links.',
      },
      {
        title: 'How we built it',
        body: 'We built the model using a dataset we found online with Python, pandas, scikit-learn, and several other libraries. We hosted the site on DreamHost and wrote the front end with HTML, CSS, and JavaScript, using Django for the backend.',
      },
      {
        title: 'Challenges & what we learned',
        body: 'We ran into poor-quality datasets where values were inaccurate by definition, which skewed our model, and as beginners we struggled with basic design. Still, we learned a lot, how to host a website, set up the file system, and code it with HTML, CSS, and JS, plus how to create and run a model. Rather than doing what we already knew, we pushed for something greater and learned from the experience, and we hope to keep developing it over time.',
      },
    ],
  },
];

// Projects shown anywhere on the site (excludes any marked `hidden: true`).
export const visibleProjects = projects.filter((p) => !p.hidden);

export const featuredProjects = visibleProjects.filter((p) => p.featured);

export const getProjectById = (id) => projects.find((p) => p.id === id);

export default visibleProjects;
